import React, { useState, useEffect, useRef } from 'react';
import './LocationReport.css';
import { getDistrict, getStates } from '../../../services/monthlyAttendanceService';
import { selectStyles } from '../../../assets/Styles/selectStyle';
import Select from 'react-select';
import { capitalizeFirstLetter  } from '../../../utils/validations';
import { getLocationHierarchyReport } from '../../../services/godownLatLongService';

import ExcelJS from 'exceljs';
import { format } from 'date-fns';
const LocationReport = () => {
    const effectRan = useRef(false);
    const [state, setState] = useState(3);
    const [district, setDistrict] = useState(null);
    const [godownType, setGodownType] = useState(null);
    const [stateOptions, setStateOptions] = useState([]);
    const [districtOptions, setDistrictOptions] = useState([]);
    const [hierarchyData, setHierarchyData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [isFocused, setIsFocused] = useState({ state: false, district: false, godownType: false });

    // Godown type options
    const godownTypeOptions = [
        { value: 'TPDS', label: 'TPDS' },
        { value: 'CMR', label: 'CMR' }
    ];

    // Fetch states on component mount
    useEffect(() => {
        const fetchStates = async () => {
            try {
                const data = await getStates();
                const sortedStates = data
                    .sort((a, b) => a.stateName.localeCompare(b.stateName))
                    .map(item => ({
                        value: item.id,
                        label: capitalizeFirstLetter(item.stateName),
                    }));
                setStateOptions(sortedStates);
            } catch (error) {
                console.error('Error fetching states:', error);
                setErrors(prev => ({ ...prev, general: 'Failed to load states' }));
            }
        };

        if (!effectRan.current) {
            fetchStates();
        }
        return () => {
            effectRan.current = true;
        };
    }, []);

    // Fetch districts when state changes
    useEffect(() => {
        if (state) {
            const fetchDistricts = async () => {
                try {
                    const data = await getDistrict(state);
                    const sortedDistricts = data
                        .sort((a, b) => a.districtName.localeCompare(b.districtName))
                        .map(item => ({
                            value: item.districtId,
                            label: item.districtName,
                        }));
                    setDistrictOptions(sortedDistricts);
                } catch (error) {
                    console.error('Error fetching districts:', error);
                    setErrors(prev => ({ ...prev, general: 'Failed to load districts' }));
                }
            };
            fetchDistricts();
        } else {
            setDistrictOptions([]);
            setDistrict(null);
        }
    }, [state]);

    // Fetch hierarchy report when submit button is clicked
    const fetchHierarchyReport = async () => {
        if (!state) {
            setErrors(prev => ({ ...prev, state: 'Please select a state' }));
            return;
        }

        setLoading(true);
        setErrors(prev => ({ ...prev, hierarchy: null }));

        try {
            const data = await getLocationHierarchyReport(state, district, godownType);
            setHierarchyData(data);
            console.log('Hierarchy Report Data:', data);
        } catch (error) {
            console.error('Error fetching hierarchy report:', error);
            setErrors(prev => ({ ...prev, hierarchy: 'Failed to load location hierarchy data' }));
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (selectedOption, field) => {
        const value = selectedOption ? selectedOption.value : null;
        switch (field) {
            case 'state':
                setState(value);
                setDistrict(null);
                setHierarchyData(null);
                break;
            case 'district':
                setDistrict(value);
                setHierarchyData(null);
                break;
            case 'godownType':
                setGodownType(value);
                setHierarchyData(null);
                break;
            default:
                break;
        }
        setErrors(prev => ({ ...prev, [field]: null }));
    };

    const handleSubmit = () => {
        fetchHierarchyReport();
    };

    const handleClear = () => {
        setState(null);
        setDistrict(null);
        setGodownType(null);
        setHierarchyData(null);
        setErrors({});
    };

    // Helper function to check if field has value
    const hasValue = (field) => {
        if (field === 'state') return state !== null && state !== undefined && state !== '';
        if (field === 'district') return district !== null && district !== undefined && district !== '';
        if (field === 'godownType') return godownType !== null && godownType !== undefined && godownType !== '';
        return false;
    };

    // Get selected label for dropdown
    const getSelectedLabel = (field, value) => {
        if (field === 'state') {
            const option = stateOptions.find(opt => opt.value === value);
            return option ? option.label : '';
        }
        if (field === 'district') {
            const option = districtOptions.find(opt => opt.value === value);
            return option ? option.label : '';
        }
        if (field === 'godownType') {
            const option = godownTypeOptions.find(opt => opt.value === value);
            return option ? option.label : '';
        }
        return '';
    };

const downloadExcel = async () => {
    if (!hierarchyData || hierarchyData.length === 0) {
        setErrors(prev => ({ ...prev, download: 'No data available to download' }));
        return;
    }

    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet('Location Report');

    const allRows = [];

    hierarchyData.forEach(state => {
        state.districts?.forEach(district => {
            district.subDivisions?.forEach(subDivision => {
                subDivision.blocks?.forEach(block => {
                    block.godowns?.forEach(godown => {
                        allRows.push([
                            capitalizeFirstLetter(state.stateName),
                            district.districtName,
                            subDivision.subDivisionName,
                            block.blockName,
                            godown.godownId,
                            godown.godownType || godown.godowntype,
                            godown.godownName,
                            godown.latitude || '-',
                            godown.longitude || '-'
                        ]);
                    });
                });
            });
        });
    });

    // =========================
    // ✅ TITLE
    // =========================
    sheet.mergeCells('A1:J1');
    const titleCell = sheet.getCell('A1');
    titleCell.value = 'GODOWN / LOCATION REPORT';
    titleCell.font = { size: 18, bold: true, color: { argb: 'FFFFFFFF' } };
    titleCell.alignment = { horizontal: 'center', vertical: 'middle' };
    titleCell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: '2E7D32' } // GREEN
    };

    // =========================
    // ✅ DATE (RIGHT SIDE)
    // =========================
    const now = new Date();
    const dateStr = now.toLocaleString('en-IN');

    sheet.mergeCells('A3:J3');
    const dateCell = sheet.getCell('J3');
    dateCell.value = `Generated On: ${format(dateStr,'dd-MM-yyy hh:mm a ')}`;
    dateCell.alignment = { horizontal: 'right' };

    // =========================
    // ✅ FILTERS
    // =========================
    sheet.mergeCells('A5:J5');
    const filterTitle = sheet.getCell('A5');
    filterTitle.value = 'SELECTED FILTERS';
    filterTitle.font = { bold: true, color: { argb: 'FFFFFFFF' } };
    filterTitle.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: '4CAF50' }
    };
    filterTitle.alignment = { horizontal: 'center' };

    sheet.addRow([
        ``,
        `State: ${getSelectedLabel('state', state) || 'N/A'}`,
        `District: ${getSelectedLabel('district', district) || 'All'}`,
        `Godown Type: ${getSelectedLabel('godownType', godownType) || 'All'}`
    ]);

    // =========================
    // ✅ HEADER ROW
    // =========================
    const headerRow = sheet.addRow([
        'S.No',
        'State',
        'District',
        'Sub Division',
        'Block',
        'Godown ID',
        'Godown Type',
        'Godown Name',
        'Latitude',
        'Longitude'
    ]);

    headerRow.eachCell((cell, colNumber) => {
        cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };

        // 🔥 S.No column ORANGE
        if (colNumber === 1) {
            cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'ED7D31' }
            };
        } else {
            cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'ED7D31' } // BLUE
            };
        }

        cell.alignment = { horizontal: 'center' };
        cell.border = {
            top: { style: 'thin' },
            bottom: { style: 'thin' },
            left: { style: 'thin' },
            right: { style: 'thin' }
        };
    });

    // =========================
    // ✅ DATA ROWS
    // =========================
    allRows.forEach((row, index) => {
        const dataRow = sheet.addRow([index + 1, ...row]);

        dataRow.eachCell((cell, colNumber) => {
            cell.alignment = {
                horizontal: colNumber === 8 ? 'left' : 'center'
            };

            // Alternate row color
            if (index % 2 === 0) {
                cell.fill = {
                    type: 'pattern',
                    pattern: 'solid',
                    fgColor: { argb: 'F2F2F2' }
                };
            }

            cell.border = {
                top: { style: 'thin' },
                bottom: { style: 'thin' },
                left: { style: 'thin' },
                right: { style: 'thin' }
            };
        });
    });

    // =========================
    // ✅ COLUMN WIDTH
    // =========================
    sheet.columns = [
        { width: 8 },
        { width: 20 },
        { width: 25 },
        { width: 25 },
        { width: 25 },
        { width: 18 },
        { width: 18 },
        { width: 35 },
        { width: 15 },
        { width: 15 }
    ];

    // =========================
    // ✅ DOWNLOAD
    // =========================
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/octet-stream' });

    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = `Godown_Report_${Date.now()}.xlsx`;
    link.click();
};
    // Render hierarchy data in table format
    const renderHierarchyTable = () => {
        if (!hierarchyData) return null;

        const allRows = [];

        hierarchyData.forEach(state => {
            state.districts?.forEach(district => {
                district.subDivisions?.forEach(subDivision => {
                    subDivision.blocks?.forEach(block => {
                        block.godowns?.forEach(godown => {
                            allRows.push({
                                stateName: capitalizeFirstLetter(state.stateName),
                                districtName: district.districtName,
                                subDivisionName: subDivision.subDivisionName,
                                blockName: block.blockName,
                                godownId: godown.godownId,
                                godowntype: godown.godownType || godown.godowntype,
                                godownName: godown.godownName,
                                latitude: godown.latitude,
                                longitude: godown.longitude
                            });
                        });
                    });
                });
            });
        });

        if (allRows.length === 0) {
            return (
                <div className="no-data-message">
                    No godowns found for the selected location.
                </div>
            );
        }

        return (
            <div className="table-responsive">
                <div className="table-header-container">
                    <button onClick={downloadExcel} className="download-excel-btn">
                         Download Excel
                    </button>
                </div>
                <table className="hierarchy-table">
                    <thead>
                        <tr>
                            <th>S.No.</th>
                            <th>State</th>
                            <th>District</th>
                            <th>Sub Division</th>
                            <th>Block</th>
                            <th>Godown ID</th>
                            <th>Godown Type</th>
                            <th>Godown Name</th>
                            <th>Latitude</th>
                            <th>Longitude</th>
                        </tr>
                    </thead>
                    <tbody>
                        {allRows.map((row, index) => (
                            <tr key={index}>
                                 <td>{index + 1}</td>
                                 <td>{row.stateName}</td>
                                 <td>{row.districtName}</td>
                                 <td>{row.subDivisionName}</td>
                                 <td>{row.blockName}</td>
                                 <td>{row.godownId}</td>
                                 <td>{row.godowntype}</td>
                                 <td>{row.godownName}</td>
                                 <td>{row.latitude || '-'}</td>
                                 <td>{row.longitude || '-'}</td>
                             </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    };

    return (
        <div>
            <div className='location-main'>
                <div className='location-box'>
                    <h2 className="location-heading">Godown/Location Report</h2>
                    <div className="location-maincontainer">
                        <div className="location-container-part">
                            <div className={`location-floating-label-container ${isFocused.state || hasValue('state') ? 'float-label' : ''}`}>
                                <Select
                                    name="state"
                                    options={stateOptions}
                                    value={stateOptions.find(opt => opt.value === state) || null}
                                    onChange={option => handleInputChange(option, 'state')}
                                    onFocus={() => setIsFocused(prev => ({ ...prev, state: true }))}
                                    onBlur={() => setIsFocused(prev => ({ ...prev, state: false }))}
                                    placeholder=" "
                                    isClearable
                                    menuPortalTarget={document.body}
                                    styles={selectStyles}
                                />
                                <label><span className="required">*</span>State</label>
                                {errors.state && <div className="error-message">{errors.state}</div>}
                            </div>
                        </div>
                        <div className="location-container-part">
                            <div className={`location-floating-label-container ${isFocused.district || hasValue('district') ? 'float-label' : ''}`}>
                                <Select
                                    name="district"
                                    options={districtOptions}
                                    value={districtOptions.find(opt => opt.value === district) || null}
                                    onChange={option => handleInputChange(option, 'district')}
                                    onFocus={() => setIsFocused(prev => ({ ...prev, district: true }))}
                                    onBlur={() => setIsFocused(prev => ({ ...prev, district: false }))}
                                    placeholder=" "
                                    isClearable
                                    menuPortalTarget={document.body}
                                    styles={selectStyles}
                                />
                                <label>District</label>
                                {errors.district && <div className="error-message">{errors.district}</div>}
                            </div>
                        </div>
                        <div className="location-container-part">
                            <div className={`location-floating-label-container ${isFocused.godownType || hasValue('godownType') ? 'float-label' : ''}`}>
                                <Select
                                    name="godownType"
                                    options={godownTypeOptions}
                                    value={godownTypeOptions.find(opt => opt.value === godownType) || null}
                                    onChange={option => handleInputChange(option, 'godownType')}
                                    onFocus={() => setIsFocused(prev => ({ ...prev, godownType: true }))}
                                    onBlur={() => setIsFocused(prev => ({ ...prev, godownType: false }))}
                                    placeholder=" "
                                    isClearable
                                    menuPortalTarget={document.body}
                                    styles={selectStyles}
                                />
                                <label>Godown Type</label>
                                {errors.godownType && <div className="error-message">{errors.godownType}</div>}
                            </div>
                        </div>
                        <div className="location-container-part">
                            <div className='sub-xl-div'>
                                <div>
                                    <button
                                        onClick={handleSubmit}
                                        className="submit-button"
                                        disabled={loading}
                                    >
                                        {loading ? 'Loading...' : 'Generate Report'}
                                    </button>
                                </div>
                                <div>
                                    <button onClick={handleClear} className="clear-button">
                                        Clear 
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {loading && (
                        <div className="loading-indicator">
                            <div className="spinner"></div>
                            <p>Loading hierarchy data...</p>
                        </div>
                    )}

                    {errors.hierarchy && (
                        <div className="error-message hierarchy-error">{errors.hierarchy}</div>
                    )}

                    {errors.download && (
                        <div className="error-message download-error">{errors.download}</div>
                    )}

                    {!loading && hierarchyData && (
                        <div className="hierarchy-table-container">
                            {renderHierarchyTable()}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default LocationReport;